import React, { useState } from "react";
import PlateApi from "../../../api/PlateApi";
import config from "../../../config";
import IconMoveToImager from "../../../Icons/Command/MovePlateToImager";
import IconMoveToLoadPort from "../../../Icons/Command/MovePlateToLoadPort";
import IconMoveToStorage from "../../../Icons/Command/MovePlateToStorage";
import { EModuleType } from "../../../Model/EModuleType";
import { PlateAddress } from "../../../Model/PlateAddress";
import {
  getModuleById,
  getModulesByType,
} from "../../../redux/reducers/ModuleReducer";
import { getPlateAddresses } from "../../../redux/reducers/PlateAddressReducer";
import { useTypedSelector } from "../../../redux/reducers/RootReducer";
import PlateUtil from "../../../Utility/PlateUtil";
import WarningView from "../../../WarningView";
import CommandPanel from "../../CommandPanel/CommandPanel";
import CommandButton from "../../CommandPanel/CommandPanelButton";
import Modal from "../../Common/Modal";
import {
  getStorageAvailability,
  getStorageFullCapacity,
  getModuleState,
  getModuleStateById,
} from "./../../../redux/reducers/ModuleStateReducer";
import { isAuthenticationEnable, isSupportModeEnabled } from "../../../redux/reducers/AuthenticationSettingReducer";

type Props = {
  style?: React.CSSProperties;
  plateAddresses: PlateAddress[];
  back: () => void;
  close: () => void;
  canMovePlateByModuleIdentifier: { [index: string]: boolean };
};

const STORAGE = "STORAGE";
const LOADPORT = "LOADPORT";
const IMAGER = "IMAGER";

const MovePlate = ({
  canMovePlateByModuleIdentifier,
  plateAddresses,
  back,
  close,
  style = {},
}: Props) => {
  const isAuthEnable: boolean = useTypedSelector((state) => isAuthenticationEnable(state));
  const isEnableSupportMode = useTypedSelector(state => isSupportModeEnabled(state));
  const moduleById = useTypedSelector((state) => getModuleById(state));
  const modulesByType = useTypedSelector((state) => getModulesByType(state));
  const imagers = modulesByType[EModuleType.IMAGER];
  const chambers = modulesByType[EModuleType.CHAMBER];
  const loadports = modulesByType[EModuleType.LOADPORT];
  const suiteState = useTypedSelector(
    (state) => state.moduleStateStore.moduleStateById["Suite"]?.suite
  );
  const alwaysShowSettingsAndTools = suiteState?.alwaysShowSettingsAndTools??false;
  const allPlateAdresses = useTypedSelector((state) =>
    getPlateAddresses(state)
  );

  let originPlateAddress = undefined;
  let originModule = EModuleType.UNKNOWN;
  let isPlateAdressInImager = false;
  const chamberIdentifiers = chambers.map((module) => module.identifier);
  const loadportIdentifiers = loadports.map((module) => module.identifier);
  const [showAlmostFullStorage, setShowAlmostFullStorage] = useState(false);
  const storageAvailability = useTypedSelector((state) =>
    getStorageAvailability(state)
  );
  const storageFullCapacity = useTypedSelector((state) =>
    getStorageFullCapacity(state)
  );

  //TODO: change code below when multi chamber implemented
  const chamberState = useTypedSelector((state) =>
    getModuleState(state, chamberIdentifiers[0])
  );
  const almostFullThresholdInPercent =
    chamberState.chamber?.almostFullCapacityThresholdInPercent ?? 0;
  const almostFullThreshold =
    almostFullThresholdInPercent < 0
      ? 0
      : almostFullThresholdInPercent > 100
        ? 1
        : almostFullThresholdInPercent / 100;

  if (plateAddresses.length === 1) {
    originPlateAddress = plateAddresses[0].usedByPlate
      ? PlateUtil.getReservedPlateAddress(
        plateAddresses[0].usedByPlate,
        allPlateAdresses
      )
      : undefined;
    isPlateAdressInImager =
      moduleById[plateAddresses[0].moduleIdentifier].moduleType ===
        EModuleType.IMAGER
        ? true
        : false;
  }

  if (originPlateAddress) {
    if (chamberIdentifiers.includes(originPlateAddress.moduleIdentifier)) {
      originModule = EModuleType.CHAMBER;
    }
    if (loadportIdentifiers.includes(originPlateAddress.moduleIdentifier)) {
      originModule = EModuleType.LOADPORT;
    }
  }

  var moveToImager = (imagerIdentifier: string) => {
    PlateApi.movePlateToImager(plateAddresses[0], imagerIdentifier);
    close();
  };

  var moveToStorage = () => {
    if (
      (chamberState.chamber?.enableAlmostFullCapacityWarning ?? false) &&
      storageAvailability <=
      storageFullCapacity -
      config.STORAGE_ALMOST_FULL_THRESHOLD * storageFullCapacity
    ) {
      setShowAlmostFullStorage(true);
    } else {
      PlateApi.movePlateToStorage(plateAddresses);
      close();
    }
  };

  const moveToLoadPort = () => {
    PlateApi.movePlateToLoadPort(plateAddresses);
    close();
  };

  const canMoveToLoadPort = () => {
    const anyReferencePlate = plateAddresses.some(p=> p.usedByPlate?.isReferencePlate ?? false);
    const allowMoveToLoadPort = !anyReferencePlate || (isAuthEnable || isEnableSupportMode || alwaysShowSettingsAndTools) ;

    return allowMoveToLoadPort && (canMovePlateByModuleIdentifier[loadportIdentifiers[0]] ?? false);
  };

  const canMoveToStorage = () => {
    return canMovePlateByModuleIdentifier[chamberIdentifiers[0]] ?? false;
  };

  const canMoveToImager = (imagerIdentifier: string) => {
    return canMovePlateByModuleIdentifier[imagerIdentifier] ?? false;
  };

  let commandItems = [];

  for (let imager of imagers) {
    commandItems.push(
      <CommandButton
        id={IMAGER + "_" + imager.name}
        icon={<IconMoveToImager />}
        content={imager.name}
        disabled={!canMoveToImager(imager.identifier)}
        onClick={() => moveToImager(imager.identifier)}
      />
    );
  }

  commandItems.push(
    <CommandButton
      id={STORAGE}
      icon={<IconMoveToStorage />}
      content={
        isPlateAdressInImager && originModule === EModuleType.CHAMBER
          ? "Return to Storage"
          : "Storage"
      }
      disabled={!canMoveToStorage()}
      onClick={moveToStorage}
    />
  );

  commandItems.push(
    <CommandButton
      id={LOADPORT}
      icon={<IconMoveToLoadPort />}
      content={
        isPlateAdressInImager && originModule === EModuleType.LOADPORT
          ? "Return to Load Port"
          : "Load Port"
      }
      disabled={!canMoveToLoadPort()}
      onClick={moveToLoadPort}
    />
  );


  return (
    <CommandPanel
      style={{
        ...style,
      }}
    >
      <CommandPanel.PlateInfoHeader plateAddresses={plateAddresses} />
      <CommandPanel.BackArrowHeader
        title="Move Plate"
        onBackArrowClick={back}
      />
      <div
        style={{
          display: "flex",
          padding: "12px 25px",
          gap: 12,
        }}
      >
        {commandItems}
      </div>
      <Modal
        show={showAlmostFullStorage}
        hideOnBackdropClick={false}
        onHide={() => setShowAlmostFullStorage(false)}
      >
        <Modal.Body>
          <WarningView
            title="Storage Warning"
            message="Plate Storage is getting full, Please remove expired plate"
            instruction={`You have ${storageAvailability} available slots`}
            onClickOk={() => {
              PlateApi.movePlateToStorage(plateAddresses);
              close();
            }}
          />
        </Modal.Body>
      </Modal>
    </CommandPanel>
  );
};

export default MovePlate;
